import React, { useState, useMemo } from 'react';
import DoneIcon from '@mui/icons-material/Done';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AccountMultiSelect from './AccountMultiSelect';
import ProductAutocomplete from './ProductAutocomplete';

export default function WastewaterPricingForm({
  initialValues,
  contextType,
  info,
  accounts,
  products,
  onSave,
  onCancel,
  onDelete,
}) {
  const [form, setForm] = useState(initialValues);
  const [saving, setSaving] = useState(false);

  const isNew = form.new;
  const fixedProductId = contextType === 'p' ? info?.id : null;
  const fixedCustomerId = contextType === 'a' ? info?.id : null;

  const productOptions = useMemo(() => {
    if (contextType === 'v') return info?.products || [];
    return products || [];
  }, [contextType, info?.products, products]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const renderFormField = (label, children, fullWidth = false) => (
    <div className={`ww-price-field${fullWidth ? ' ww-price-field-full' : ''}`}>
      <label>{label}</label>
      {children}
    </div>
  );

  const renderFixedValue = (value) => (
    <div className="ww-price-fixed-value">{value || '—'}</div>
  );

  const renderFormInput = (field, placeholder, type = 'text', { step } = {}) => (
    <input
      className="ww-price-input"
      type={type}
      placeholder={placeholder}
      step={step}
      value={form[field] ?? ''}
      onChange={(e) => updateField(field, e.target.value)}
    />
  );

  const renderProductField = () => {
    if (fixedProductId) {
      return renderFixedValue(form.product_model || info?.model);
    }

    if (isNew) {
      return (
        <ProductAutocomplete
          products={productOptions}
          productId={form.product_id}
          productLabel={form.product_model}
          onChange={(product) => setForm((prev) => ({
            ...prev,
            product_id: product?.id || '',
            product_model: product?.model || '',
          }))}
          placeholder={contextType === 'v' ? 'Search vendor products' : 'Search products'}
        />
      );
    }

    return renderFixedValue(form.product_model);
  };

  const renderAccountField = () => {
    if (fixedCustomerId) {
      return renderFixedValue(form.customer_name || info?.name);
    }

    if (isNew) {
      return (
        <AccountMultiSelect
          accounts={accounts}
          selected={form.selected_accounts || []}
          onChange={(selected_accounts) => updateField('selected_accounts', selected_accounts)}
          placeholder="Search accounts to add"
        />
      );
    }

    return renderFixedValue(form.customer_name);
  };

  const saveLabel = () => {
    if (!isNew) return 'Save';
    const accountCount = fixedCustomerId ? 1 : (form.selected_accounts || []).length;
    return accountCount > 1 ? `Save (${accountCount} accounts)` : 'Save';
  };

  const isSaveDisabled = isNew && (
    (!fixedCustomerId && !(form.selected_accounts || []).length) ||
    (!fixedProductId && !form.product_id)
  );

  const handleSave = async () => {
    const payload = {
      ...form,
      product_id: fixedProductId || form.product_id,
      context_type: contextType,
      context_id: info?.id,
    };

    delete payload.quantity;
    delete payload.moq_unit_of_measure;

    if (isNew && !fixedCustomerId) {
      payload.customer_ids = (form.selected_accounts || []).map((account) => account.id);
      delete payload.customer_id;
      delete payload.selected_accounts;
    } else {
      payload.customer_id = fixedCustomerId || form.customer_id;
      delete payload.selected_accounts;
    }

    setSaving(true);
    try {
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ww-price-form">
      <div className="ww-price-form-header">
        <h4>{isNew ? 'New pricing record' : 'Edit pricing record'}</h4>
        <button type="button" className="ww-price-icon-btn" onClick={onCancel} aria-label="Close form">
          <CloseIcon />
        </button>
      </div>

      <div className="ww-price-form-grid">
        {renderFormField('Product', renderProductField(), isNew && !fixedProductId)}

        {renderFormField(
          isNew ? 'Accounts' : 'Account',
          renderAccountField(),
          isNew && !fixedCustomerId
        )}

        {renderFormField(
          'Price per unit',
          <div className="ww-price-per-unit-input">
            <span className="ww-price-per-unit-prefix" aria-hidden="true">$</span>
            <input
              className="ww-price-input ww-price-per-unit-amount"
              type="number"
              placeholder="0.0000"
              step="0.0001"
              value={form.price ?? ''}
              onChange={(e) => updateField('price', e.target.value)}
            />
            <span className="ww-price-per-unit-separator" aria-hidden="true">/</span>
            <input
              className="ww-price-input ww-price-per-unit-uom"
              type="text"
              placeholder="e.g. gal, lb"
              value={form.unit_of_measure ?? ''}
              onChange={(e) => updateField('unit_of_measure', e.target.value)}
            />
          </div>,
          true
        )}

        <div className="ww-price-form-row ww-price-form-row-2">
          {renderFormField('MOQ', renderFormInput('moq', '1', 'number'))}
          {renderFormField('Container type', renderFormInput('container_type', 'e.g. drum, tote'))}
        </div>

        <div className="ww-price-form-row ww-price-form-row-2">
          {renderFormField(
            'Effective start',
            renderFormInput('pricing_effective_start_date', '', 'date')
          )}
          {renderFormField(
            'Effective end',
            renderFormInput('pricing_effective_end_date', '', 'date')
          )}
        </div>

        {renderFormField(
          'Notes',
          <textarea
            className="ww-price-textarea"
            placeholder="Optional notes"
            value={form.notes ?? ''}
            onChange={(e) => updateField('notes', e.target.value)}
            rows={3}
          />
        )}
      </div>

      <div className="ww-price-form-actions">
        <div className="ww-price-btn-wrap">
          {saving && <div className="saving-flash shown" aria-hidden="true" />}
          <button
            type="button"
            className="ww-price-btn ww-price-btn-primary"
            onClick={handleSave}
            disabled={isSaveDisabled || saving}
          >
            <DoneIcon />
            {saving ? 'Saving...' : saveLabel()}
          </button>
        </div>
        <button type="button" className="ww-price-btn ww-price-btn-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        {!isNew && (
          <button type="button" className="ww-price-btn ww-price-btn-danger" onClick={() => onDelete(form)}>
            <DeleteIcon />
            Delete
          </button>
        )}
      </div>
    </div>
  );
}
